import { Check, Minus, ArrowDown, ArrowRight, RotateCcw } from "lucide-react";

const stages = [
  "Strategy & ICP",
  "Prospect discovery",
  "Data verification",
  "AI research",
  "Outreach & follow-up",
  "Qualification & CRM",
] as const;

const serviceModels = [
  {
    name: "Data-only support",
    description: "A defined scope for finding and verifying contacts.",
    scope: [false, true, true, false, false, false],
    connected: false,
  },
  {
    name: "Outreach-only support",
    description: "A defined scope for sending messages and follow-ups.",
    scope: [false, false, false, false, true, false],
    connected: false,
  },
  {
    name: "UniqReach",
    description: "A connected system built around your sales process.",
    scope: [true, true, true, true, true, true],
    connected: true,
  },
] as const;

const workflows = [
  {
    name: "A finite campaign",
    takeaway: "Reporting closes the cycle.",
    looping: false,
    steps: [
      { label: "Plan", detail: "Define the campaign" },
      { label: "Run", detail: "Execute outreach" },
      { label: "Report", detail: "Summarize activity" },
      { label: "Finish", detail: "Close the campaign" },
    ],
  },
  {
    name: "The UniqReach system",
    takeaway: "Review informs the next cycle.",
    looping: true,
    steps: [
      { label: "Plan", detail: "Agree goals & ICP" },
      { label: "Run", detail: "Connect sales stages" },
      { label: "Review", detail: "Assess conversations" },
      { label: "Improve", detail: "Refine data & messaging" },
    ],
  },
] as const;

export function ServiceComparisonCharts() {
  return (
    <div className="service-comparison-charts">
      <figure
        className="service-chart"
        aria-labelledby="coverage-chart-title"
        aria-describedby="coverage-chart-note"
      >
        <div className="service-chart-heading">
          <span className="label">01 / Service coverage</span>
          <h3 id="coverage-chart-title">
            A task solves one part. A system connects the journey.
          </h3>
          <p>
            Compare a limited data or outreach scope with the connected sales
            system UniqReach can build.
          </p>
        </div>
        <table className="scope-chart-table" role="table">
          <caption className="visually-hidden">
            Service model coverage across six sales stages
          </caption>
          <thead role="rowgroup">
            <tr role="row">
              <th scope="col" role="columnheader">
                Service model
              </th>
              {stages.map((stage) => (
                <th scope="col" role="columnheader" key={stage}>
                  {stage}
                </th>
              ))}
            </tr>
          </thead>
          <tbody role="rowgroup">
            {serviceModels.map((model) => (
              <tr
                role="row"
                key={model.name}
                className={model.connected ? "scope-model-connected" : ""}
              >
                <th scope="row" role="rowheader">
                  <span>{model.name}</span>
                  <p>{model.description}</p>
                </th>
                {stages.map((stage, index) => (
                  <td role="cell" key={stage}>
                    <span className="scope-mobile-stage" aria-hidden="true">
                      {stage}
                    </span>
                    <span
                      className={`scope-mark ${model.scope[index] ? "is-included" : "is-outside"}`}
                    >
                      {model.scope[index] ? (
                        <Check size={18} aria-hidden="true" />
                      ) : (
                        <Minus size={18} aria-hidden="true" />
                      )}
                      <span>
                        {model.scope[index] ? "In scope" : "Outside scope"}
                      </span>
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <figcaption id="coverage-chart-note" className="service-chart-note">
          Schematic service models, not an audit of individual providers.
          UniqReach coverage shows the system we can build; the agreed scope
          depends on your goals and tools.
        </figcaption>
      </figure>

      <figure
        className="service-chart"
        aria-labelledby="workflow-chart-title"
        aria-describedby="workflow-chart-note"
      >
        <div className="service-chart-heading">
          <span className="label">02 / The feedback loop</span>
          <h3 id="workflow-chart-title">
            Keep the learning connected to the work.
          </h3>
          <p>
            The difference is what happens after outreach: close a campaign, or
            use the evidence to improve the system.
          </p>
        </div>
        <div className="workflow-comparison">
          {workflows.map((workflow) => (
            <div
              className={`workflow-lane ${workflow.looping ? "workflow-lane-connected" : ""}`}
              key={workflow.name}
            >
              <div className="workflow-lane-heading">
                <h4>{workflow.name}</h4>
                <p>{workflow.takeaway}</p>
              </div>
              <ol
                className="workflow-path"
                aria-label={`${workflow.name} stages`}
              >
                {workflow.steps.map((step, index) => (
                  <li key={step.label}>
                    <span className="workflow-node-index" aria-hidden="true">
                      0{index + 1}
                    </span>
                    <strong>{step.label}</strong>
                    <span>{step.detail}</span>
                    {index < workflow.steps.length - 1 && (
                      <span className="workflow-direction" aria-hidden="true">
                        <ArrowRight
                          className="workflow-arrow-horizontal"
                          size={16}
                        />
                        <ArrowDown
                          className="workflow-arrow-vertical"
                          size={16}
                        />
                      </span>
                    )}
                  </li>
                ))}
              </ol>
              <div
                className={
                  workflow.looping ? "workflow-return" : "workflow-end"
                }
              >
                {workflow.looping ? (
                  <RotateCcw size={18} aria-hidden="true" />
                ) : (
                  <span className="workflow-end-dot" aria-hidden="true" />
                )}
                <span>
                  {workflow.looping
                    ? "Feed insights back into the next plan"
                    : "Campaign complete"}
                </span>
              </div>
            </div>
          ))}
        </div>
        <figcaption id="workflow-chart-note" className="service-chart-note">
          Process comparison based on our described approach, not a performance
          forecast. Other providers may also offer ongoing optimization.
        </figcaption>
      </figure>
    </div>
  );
}
